import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import EventRegistration from "@/pages/EventRegistration";
import RelayRegistration from "@/pages/RelayRegistration";
import Payment from "@/pages/Payment";
import Accommodation from "@/pages/Accommodation";
import Logistics from "@/pages/Logistics";
import Meals from "@/pages/Meals";
import CityGuide from "@/pages/CityGuide";
import Documents from "@/pages/Documents";
import AccommodationChoice from "@/pages/AccommodationChoice";
import AdministrativeFee from "@/pages/AdministrativeFee";
import Unauthorized from "@/pages/Unauthorized";
import NotFound from "@/pages/NotFound";
import ErrorBoundary from "@/components/ErrorBoundary";
import Results from "@/pages/Results";
import HeatList from "@/pages/HeatList";
import Schedule from "@/pages/Schedule";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import CookiePolicy from "@/pages/CookiePolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import GlobalPopup from "@/components/GlobalPopup";

// Staff-only / rarely-visited pages, code-split out of the main bundle.
// These pull in heavy libs (html2canvas, jspdf, qrcode.react, jsQR) that a
// typical athlete/public visitor never needs — no reason to ship them on
// every Landing-page load.
const IdCard = React.lazy(() => import("@/pages/IdCard"));
const Certificate = React.lazy(() => import("@/pages/Certificate"));
const InvoiceAccommodation = React.lazy(() => import("@/pages/InvoiceAccommodation"));
const InvoiceLogistics = React.lazy(() => import("@/pages/InvoiceLogistics"));
const InvoiceRegistration = React.lazy(() => import("@/pages/InvoiceRegistration"));
const InvoiceRelay = React.lazy(() => import("@/pages/InvoiceRelay"));
const InvoiceMeal = React.lazy(() => import("@/pages/InvoiceMeal"));
const TeamLogistics = React.lazy(() => import("@/pages/TeamLogistics"));
const TeamAccommodation = React.lazy(() => import("@/pages/TeamAccommodation"));
const TeamSecurity = React.lazy(() => import("@/pages/TeamSecurity"));
const TeamDelegate = React.lazy(() => import("@/pages/TeamDelegate"));
const Admin = React.lazy(() => import("@/pages/Admin"));
const SecurityScanner = React.lazy(() => import("@/pages/SecurityScanner"));
const GeneralSecurityScanner = React.lazy(() => import("@/pages/GeneralSecurityScanner"));

const Agentation = process.env.NODE_ENV === "development"
  ? React.lazy(() => import("agentation").then((mod) => ({ default: mod.Agentation })))
  : null;

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0B1429]">
    <div className="w-10 h-10 rounded-full border-2 border-[#00F0FF]/30 border-t-[#00F0FF] animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <React.Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/city-guide" element={<CityGuide />} />
            <Route path="/results" element={<Results />} />
            <Route path="/heat-list" element={<HeatList />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route
              path="/team/security"
              element={
                <ProtectedRoute allowedRoles={["security_team", "admin"]}>
                  <TeamSecurity />
                </ProtectedRoute>
              }
            />
            <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["athlete"]}><Dashboard /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute allowedRoles={["athlete", "admin"]}><EventRegistration /></ProtectedRoute>} />
            <Route path="/relays" element={<ProtectedRoute allowedRoles={["athlete", "admin"]}><RelayRegistration /></ProtectedRoute>} />
            <Route path="/payment" element={<ProtectedRoute allowedRoles={["athlete"]}><Payment /></ProtectedRoute>} />
            <Route path="/payment/:regId" element={<ProtectedRoute allowedRoles={["athlete"]}><Payment /></ProtectedRoute>} />
            <Route path="/accommodation" element={<ProtectedRoute allowedRoles={["athlete", "admin"]}><Accommodation /></ProtectedRoute>} />
            <Route path="/accommodation-choice" element={<ProtectedRoute allowedRoles={["athlete"]}><AccommodationChoice /></ProtectedRoute>} />
            <Route path="/administrative-fee" element={<ProtectedRoute allowedRoles={["athlete"]}><AdministrativeFee /></ProtectedRoute>} />
            <Route path="/logistics" element={<ProtectedRoute allowedRoles={["athlete", "admin"]}><Logistics /></ProtectedRoute>} />
            <Route path="/meals" element={<ProtectedRoute allowedRoles={["athlete"]}><Meals /></ProtectedRoute>} />
            <Route path="/profile/documents" element={<ProtectedRoute allowedRoles={["athlete"]}><Documents /></ProtectedRoute>} />
            <Route path="/id-card" element={<ProtectedRoute allowedRoles={["athlete"]}><IdCard /></ProtectedRoute>} />
            <Route path="/certificate" element={<ProtectedRoute allowedRoles={["athlete"]}><Certificate /></ProtectedRoute>} />
            <Route path="/invoice/accommodation/:bookingId" element={<ProtectedRoute allowedRoles={["athlete"]}><InvoiceAccommodation /></ProtectedRoute>} />
            <Route path="/invoice/logistics/:requestId" element={<ProtectedRoute allowedRoles={["athlete"]}><InvoiceLogistics /></ProtectedRoute>} />
            <Route path="/invoice/registration/:paymentId" element={<ProtectedRoute allowedRoles={["athlete"]}><InvoiceRegistration /></ProtectedRoute>} />
            <Route path="/invoice/relay/:paymentId" element={<ProtectedRoute allowedRoles={["athlete"]}><InvoiceRelay /></ProtectedRoute>} />
            <Route path="/invoice/meals/:bookingId" element={<ProtectedRoute allowedRoles={["athlete"]}><InvoiceMeal /></ProtectedRoute>} />
            <Route path="/team/logistics" element={<ProtectedRoute allowedRoles={["logistics_team", "admin"]}><TeamLogistics /></ProtectedRoute>} />
            <Route path="/team/accommodation" element={<ProtectedRoute allowedRoles={["accommodation_team", "admin"]}><TeamAccommodation /></ProtectedRoute>} />
            <Route path="/team/delegate" element={<ProtectedRoute allowedRoles={["delegate", "admin"]}><TeamDelegate /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><Admin /></ProtectedRoute>} />
            <Route
              path="/team/security/scanner/:eventId"
              element={
                <ProtectedRoute allowedRoles={["security_team", "admin"]}>
                  <SecurityScanner />
                </ProtectedRoute>
              }
            />
            <Route
              path="/team/security/general-scanner"
              element={
                <ProtectedRoute allowedRoles={["security_team", "admin"]}>
                  <GeneralSecurityScanner />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </React.Suspense>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors closeButton />
        <GlobalPopup />
        {Agentation && (
          <React.Suspense fallback={null}>
            <Agentation />
          </React.Suspense>
        )}
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
